import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAdminUser, ADMIN_CREDENTIALS } from '../utils/createAdminUser';
import {
  Shield,
  User,
  Mail,
  Lock,
  CheckCircle,
  AlertCircle,
  Copy,
  ExternalLink,
  Database
} from 'lucide-react';

const AdminSetup = () => {
  const [setupStatus, setSetupStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSetupAdmin = async () => {
    setLoading(true);
    try {
      const result = await createAdminUser();
      setSetupStatus(result);
    } catch (error) {
      setSetupStatus({
        success: false,
        error: error.message
      });
    }
    setLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Setup</h1>
          <p className="text-gray-600 mt-2">Set up your admin account for Gifted Solutions</p>
        </div>

        {/* Admin Credentials Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <User className="mr-2" size={20} />
            Admin Credentials
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <Mail className="w-4 h-4 text-gray-500 mr-2" />
                <span className="text-sm text-gray-600">Email:</span>
              </div>
              <div className="flex items-center">
                <code className="text-sm font-mono bg-white px-2 py-1 rounded border">
                  {ADMIN_CREDENTIALS.email}
                </code>
                <button
                  onClick={() => copyToClipboard(ADMIN_CREDENTIALS.email)}
                  className="ml-2 p-1 text-gray-400 hover:text-gray-600"
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <Lock className="w-4 h-4 text-gray-500 mr-2" />
                <span className="text-sm text-gray-600">Password:</span>
              </div>
              <div className="flex items-center">
                <code className="text-sm font-mono bg-white px-2 py-1 rounded border">
                  {ADMIN_CREDENTIALS.password}
                </code>
                <button
                  onClick={() => copyToClipboard(ADMIN_CREDENTIALS.password)}
                  className="ml-2 p-1 text-gray-400 hover:text-gray-600"
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Setup Button */}
        {!setupStatus && (
          <button
            onClick={handleSetupAdmin}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center mb-4"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating Admin...
              </>
            ) : (
              <>
                <Database className="mr-2" size={16} />
                Create Admin Account
              </>
            )}
          </button>
        )}

        {/* Setup Status */}
        {setupStatus && (
          <div className={`p-4 rounded-lg mb-4 ${
            setupStatus.success 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center">
              {setupStatus.success ? (
                <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              )}
              <span className={`font-medium ${
                setupStatus.success ? 'text-green-800' : 'text-red-800'
              }`}>
                {setupStatus.success 
                  ? 'Admin account created successfully!' 
                  : setupStatus.error || 'Setup failed'
                }
              </span>
            </div>
            
            {setupStatus.success && (
              <p className="text-green-700 text-sm mt-2">
                You can now login with the credentials above.
              </p>
            )}
          </div>
        )}

        {/* Quick Actions */}
        <div className="space-y-3">
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 flex items-center justify-center"
          >
            <ExternalLink className="mr-2" size={16} />
            Go to Login Page
          </button>
          
          <button
            onClick={() => navigate('/admin-dashboard')}
            className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 flex items-center justify-center"
          >
            <Shield className="mr-2" size={16} />
            Access Admin Dashboard
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 flex items-center justify-center"
          >
            Back to Website
          </button>
        </div>

        {/* Instructions */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="font-medium text-blue-900 mb-2">Instructions:</h3>
          <ol className="text-sm text-blue-800 space-y-1">
            <li>1. Click "Create Admin Account" to set up the admin user</li>
            <li>2. Use the credentials above to login</li>
            <li>3. Access the admin dashboard to manage your site</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default AdminSetup;
